import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { createFunderProfile, getCountries, getSectors, getFunderTypes } from '@/lib/api';
import {
  Landmark,
  ChevronRight,
  ChevronLeft,
  Check,
  Target,
  DollarSign,
  Mail,
  Sparkles,
  TrendingUp,
  MapPin,
  Building2,
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_creditai-africa/artifacts/q9fyvy5m_ChatGPT%20Image%20Feb%201%2C%202026%2C%2012_34_17%20PM.png";

const STEPS = [
  { id: 1, title: 'Organization', icon: Landmark, description: 'Basic organization details' },
  { id: 2, title: 'Investment Focus', icon: Target, description: 'Regions and sectors' },
  { id: 3, title: 'Deal Criteria', icon: DollarSign, description: 'Ticket size and preferences' },
  { id: 4, title: 'Contact', icon: Mail, description: 'Contact information' },
];

const REGIONS = [
  { id: 'Nigeria', label: 'Nigeria' },
  { id: 'Kenya', label: 'Kenya' },
  { id: 'Ghana', label: 'Ghana' },
  { id: 'South Africa', label: 'South Africa' },
  { id: 'Tanzania', label: 'Tanzania' },
  { id: 'Uganda', label: 'Uganda' },
  { id: 'Rwanda', label: 'Rwanda' },
  { id: 'Egypt', label: 'Egypt' },
  { id: 'Ethiopia', label: 'Ethiopia' },
  { id: 'Morocco', label: 'Morocco' },
  { id: 'Senegal', label: 'Senegal' },
  { id: 'Côte d\'Ivoire', label: 'Côte d\'Ivoire' },
];

const CURRENCIES = [
  { id: 'USD', label: 'USD' },
  { id: 'EUR', label: 'EUR' },
  { id: 'GBP', label: 'GBP' },
  { id: 'NGN', label: 'NGN' },
  { id: 'KES', label: 'KES' },
  { id: 'ZAR', label: 'ZAR' },
];

const TENORS = [
  { id: 6, label: '6 mo' },
  { id: 12, label: '12 mo' },
  { id: 24, label: '24 mo' },
  { id: 36, label: '36 mo' },
  { id: 48, label: '48 mo' },
  { id: 60, label: '60 mo' },
];

export default function FunderOnboarding({ onComplete }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [funderTypes, setFunderTypes] = useState([]);
  
  const [formData, setFormData] = useState({
    organization_name: '',
    funder_type: '',
    country: '',
    description: '',
    regions_served: [],
    sectors_focus: [],
    min_ticket_size: '',
    max_ticket_size: '',
    preferred_tenors: [],
    currency_preferences: [],
    risk_appetite: 'MODERATE',
    contact_email: '',
    contact_phone: '',
  });

  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const [countriesData, sectorsData, funderTypesData] = await Promise.all([
          getCountries(),
          getSectors(),
          getFunderTypes(),
        ]);
        setCountries(countriesData);
        setSectors(sectorsData);
        setFunderTypes(funderTypesData);
      } catch (error) {
        console.error('Error loading reference data:', error);
      }
    };
    loadReferenceData();
  }, []);

  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleArrayItem = (field, item) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter(i => i !== item)
        : [...prev[field], item]
    }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.organization_name.trim() !== '' && formData.funder_type !== '' && formData.country !== '';
      case 2:
        return formData.regions_served.length > 0 || formData.sectors_focus.length > 0;
      case 3:
        return true;
      case 4:
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (!validateStep(currentStep)) {
      if (currentStep === 1) {
        toast.error('Please fill in organization name, type, and country');
      } else if (currentStep === 2) {
        toast.error('Please select at least one region or sector focus');
      }
      return;
    }
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const data = {
        organization_name: formData.organization_name,
        funder_type: formData.funder_type,
        country: formData.country,
        description: formData.description || null,
        regions_served: formData.regions_served,
        sectors_focus: formData.sectors_focus,
        min_ticket_size: formData.min_ticket_size ? parseFloat(formData.min_ticket_size) : null,
        max_ticket_size: formData.max_ticket_size ? parseFloat(formData.max_ticket_size) : null,
        preferred_tenors: formData.preferred_tenors.map(Number),
        currency_preferences: formData.currency_preferences,
        risk_appetite: formData.risk_appetite,
        contact_email: formData.contact_email || null,
        contact_phone: formData.contact_phone || null,
      };

      await createFunderProfile(data);
      toast.success('Profile created! Start receiving matched opportunities.');
      
      if (onComplete) {
        onComplete();
      } else {
        navigate('/funder');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create profile');
    } finally {
      setLoading(false);
    }
  };

  const progressPercentage = ((currentStep) / STEPS.length) * 100;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <img src={LOGO_URL} alt="Apaka" className="h-12" />
          <button
            onClick={() => navigate('/funder')}
            className="text-sm text-slate-500 hover:text-slate-700"
          >
            Skip for now
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Welcome Message */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-heading font-bold text-slate-900 mb-2">
            Welcome, {user?.full_name?.split(' ')[0]}! 👋
          </h1>
          <p className="text-slate-600">Set up your funder profile to receive matched opportunities</p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">Profile Setup Progress</span>
            <span className="text-sm text-slate-500">{Math.round(progressPercentage)}% Complete</span>
          </div>
          <Progress value={progressPercentage} className="h-2 [&>div]:bg-emerald-500" />
        </div>

        {/* Step Indicators */}
        <div className="flex items-center justify-between mb-8 overflow-x-auto pb-2">
          {STEPS.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <button
                onClick={() => currentStep > step.id && setCurrentStep(step.id)}
                disabled={currentStep < step.id}
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                  currentStep === step.id
                    ? 'bg-emerald-600 text-white'
                    : currentStep > step.id
                    ? 'bg-emerald-100 text-emerald-700 cursor-pointer hover:bg-emerald-200'
                    : 'bg-slate-100 text-slate-400'
                }`}
              >
                {currentStep > step.id ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <step.icon className="h-4 w-4" />
                )}
                <span className="text-sm font-medium whitespace-nowrap">{step.title}</span>
              </button>
              {index < STEPS.length - 1 && (
                <div className={`hidden sm:block w-8 h-0.5 mx-2 ${
                  currentStep > step.id ? 'bg-emerald-300' : 'bg-slate-200'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Form Card */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center gap-3">
              {(() => {
                const StepIcon = STEPS[currentStep - 1].icon;
                return (
                  <div className="p-2 bg-emerald-50 rounded-lg">
                    <StepIcon className="h-5 w-5 text-emerald-600" />
                  </div>
                );
              })()}
              <div>
                <CardTitle className="text-lg">{STEPS[currentStep - 1].title}</CardTitle>
                <CardDescription>{STEPS[currentStep - 1].description}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {/* Step 1: Organization */}
            {currentStep === 1 && (
              <div className="space-y-6 animate-fade-in">
                <div className="space-y-2">
                  <Label>Organization Name <span className="text-red-500">*</span></Label>
                  <Input
                    value={formData.organization_name}
                    onChange={(e) => updateForm('organization_name', e.target.value)}
                    placeholder="e.g., African Development Fund"
                    data-testid="funder-onboard-org-name"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Organization Type <span className="text-red-500">*</span></Label>
                    <Select value={formData.funder_type} onValueChange={(v) => updateForm('funder_type', v)}>
                      <SelectTrigger data-testid="funder-onboard-type">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {funderTypes.map((t) => (
                          <SelectItem key={t} value={t}>{t.replace('_', ' ')}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Headquarters Country <span className="text-red-500">*</span></Label>
                    <Select value={formData.country} onValueChange={(v) => updateForm('country', v)}>
                      <SelectTrigger data-testid="funder-onboard-country">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        {countries.map((c) => (
                          <SelectItem key={c.code} value={c.name}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => updateForm('description', e.target.value)}
                    placeholder="Tell us about your organization and investment mandate..."
                  />
                </div>
              </div>
            )}

            {/* Step 2: Investment Focus */}
            {currentStep === 2 && (
              <div className="space-y-6 animate-fade-in">
                <div className="space-y-3">
                  <Label>Regions of Interest <span className="text-red-500">*</span></Label>
                  <p className="text-xs text-slate-500">Select all regions where you invest</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {REGIONS.map((region) => (
                      <button
                        key={region.id}
                        type="button"
                        onClick={() => toggleArrayItem('regions_served', region.id)}
                        className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${
                          formData.regions_served.includes(region.id)
                            ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                            : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        <MapPin className="h-4 w-4" />
                        <span className="text-sm">{region.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <Label>Sector Focus <span className="text-red-500">*</span></Label>
                  <p className="text-xs text-slate-500">Select your preferred investment sectors</p>
                  <div className="grid grid-cols-2 gap-2">
                    {sectors.map((sector) => (
                      <button
                        key={sector.id}
                        type="button"
                        onClick={() => toggleArrayItem('sectors_focus', sector.name)}
                        className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${
                          formData.sectors_focus.includes(sector.name)
                            ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                            : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        <Building2 className="h-4 w-4" />
                        <span className="text-sm">{sector.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Target className="h-5 w-5 text-emerald-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-emerald-800 font-medium">Smart Matching</p>
                      <p className="text-xs text-emerald-600 mt-1">
                        Your focus areas help us match you with relevant funding opportunities from companies in your target regions and sectors.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Deal Criteria */}
            {currentStep === 3 && (
              <div className="space-y-6 animate-fade-in">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Min Ticket Size (USD)</Label>
                    <Select value={formData.min_ticket_size} onValueChange={(v) => updateForm('min_ticket_size', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select minimum" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="50000">$50,000</SelectItem>
                        <SelectItem value="100000">$100,000</SelectItem>
                        <SelectItem value="250000">$250,000</SelectItem>
                        <SelectItem value="500000">$500,000</SelectItem>
                        <SelectItem value="1000000">$1,000,000</SelectItem>
                        <SelectItem value="5000000">$5,000,000</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Max Ticket Size (USD)</Label>
                    <Select value={formData.max_ticket_size} onValueChange={(v) => updateForm('max_ticket_size', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select maximum" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="500000">$500,000</SelectItem>
                        <SelectItem value="1000000">$1,000,000</SelectItem>
                        <SelectItem value="5000000">$5,000,000</SelectItem>
                        <SelectItem value="10000000">$10,000,000</SelectItem>
                        <SelectItem value="25000000">$25,000,000</SelectItem>
                        <SelectItem value="50000000">$50,000,000+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-3">
                  <Label>Preferred Tenors</Label>
                  <div className="flex flex-wrap gap-2">
                    {TENORS.map((tenor) => (
                      <button
                        key={tenor.id}
                        type="button"
                        onClick={() => toggleArrayItem('preferred_tenors', tenor.id)}
                        className={`px-4 py-2 rounded-full border transition-all ${
                          formData.preferred_tenors.includes(tenor.id)
                            ? 'bg-emerald-600 border-emerald-600 text-white'
                            : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        {tenor.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <Label>Currency Preferences</Label>
                  <div className="flex flex-wrap gap-2">
                    {CURRENCIES.map((currency) => (
                      <button
                        key={currency.id}
                        type="button"
                        onClick={() => toggleArrayItem('currency_preferences', currency.id)}
                        className={`px-4 py-2 rounded-full border transition-all ${
                          formData.currency_preferences.includes(currency.id)
                            ? 'bg-emerald-600 border-emerald-600 text-white'
                            : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        {currency.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Risk Appetite</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {['CONSERVATIVE', 'MODERATE', 'AGGRESSIVE'].map((appetite) => (
                      <button
                        key={appetite}
                        type="button"
                        onClick={() => updateForm('risk_appetite', appetite)}
                        className={`p-3 rounded-lg border text-center transition-all ${
                          formData.risk_appetite === appetite
                            ? appetite === 'CONSERVATIVE' ? 'bg-blue-50 border-blue-300 text-blue-700' :
                              appetite === 'MODERATE' ? 'bg-amber-50 border-amber-300 text-amber-700' :
                              'bg-red-50 border-red-300 text-red-700'
                            : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        <TrendingUp className={`h-5 w-5 mx-auto mb-1 ${
                          formData.risk_appetite === appetite
                            ? appetite === 'CONSERVATIVE' ? 'text-blue-600' :
                              appetite === 'MODERATE' ? 'text-amber-600' : 'text-red-600'
                            : 'text-slate-400'
                        }`} />
                        <span className="text-sm capitalize">{appetite.toLowerCase()}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Contact */}
            {currentStep === 4 && (
              <div className="space-y-6 animate-fade-in">
                <div className="space-y-2">
                  <Label>Deal Contact Email</Label>
                  <Input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => updateForm('contact_email', e.target.value)}
                    placeholder="deals@yourorganization.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone Number</Label>
                  <Input
                    value={formData.contact_phone}
                    onChange={(e) => updateForm('contact_phone', e.target.value)}
                    placeholder="+1 555 000 0000"
                  />
                </div>
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Sparkles className="h-5 w-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-amber-800 font-medium">You're all set!</p>
                      <p className="text-xs text-amber-600 mt-1">
                        Once your profile is complete, you'll start receiving matched opportunities from companies seeking funding that fit your criteria.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-slate-100">
              <Button
                variant="ghost"
                onClick={prevStep}
                disabled={currentStep === 1}
                className="text-slate-600"
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              
              {currentStep < STEPS.length ? (
                <Button
                  onClick={nextStep}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="funder-onboard-next"
                >
                  Continue
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="funder-onboard-complete"
                >
                  {loading ? 'Creating Profile...' : 'Complete Setup'}
                  <Check className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
